import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, Checkbox, FormFeedback } from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import {
  FormFieldDefinition,
  FormFieldCheckbox
} from '@publishing-engine/domain/types/article';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { generateValidationSchema } from '../../../../utils/validation';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks';

interface Props {
  isOpen: boolean;
  onSubmit: (value: boolean) => void;
  onCancel: () => void;
  formField: FormFieldCheckbox;
  value: boolean;
}

type ICheckboxFormState = {
  [key: string]: boolean;
};

const CheckBoxUpdateModal: React.FC<Props> = props => {
  const { isOpen, onSubmit, onCancel, formField, value } = props;

  // Schema validation
  const schema = generateValidationSchema({
    [formField.name]: formField
  } as Record<string, FormFieldDefinition>);

  const methods = useForm({
    defaultValues: _.set(
      {} as ICheckboxFormState,
      formField.caseDataFields.main,
      value
    ),
    resolver: ajvResolver(schema)
  });

  const {
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors }
  } = methods;

  const fieldName = formField.caseDataFields.main;
  const mainField = `${fieldName}` as const;
  const fieldValue: boolean = watch(mainField) ?? false;

  const handleCheckboxChange = (checked: boolean) => {
    clearErrors(mainField);
    setValue(mainField, checked, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex
        padding={8}
        gap={4}
        bgColor="surfacePrimaryFill"
        flexDirection="column"
        borderRadius="base"
      >
        <form
          onSubmit={handleSubmit(() => {
            onSubmit(fieldValue);
          })}
        >
          <Flex marginBottom={4}>
            <Text textStyle="headingSection">{`Update ${formField.params.labels.main}`}</Text>
          </Flex>

          <Flex
            padding={8}
            gap={6}
            bgColor="surfacePrimaryFill"
            flexDirection="column"
            borderRadius="base"
            flex={1}
          >
            <Flex gap={4} flexDirection="column" marginBottom={4}>
              {formField.params.labels.subtitle && (
                <Text textStyle="textBody">
                  {parseStringWithLinks(formField.params.labels.subtitle)}
                </Text>
              )}
              <Checkbox
                id={mainField}
                name={mainField}
                ariaLabel={mainField}
                isChecked={fieldValue}
                onChange={({ target: { checked } }) =>
                  handleCheckboxChange(checked)
                }
              >
                <Flex flexDirection="column">
                  {formField.params.labels.content && (
                    <Text textStyle="textBody">
                      {formField.params.labels.content}
                    </Text>
                  )}
                </Flex>
              </Checkbox>
            </Flex>
          </Flex>
          {_.get(errors, mainField) && (
            <FormFeedback variant="error">
              Confirmation is required
            </FormFeedback>
          )}
          <Flex
            gap={2}
            justifyContent="flex-end"
            alignItems="center"
            alignSelf="stretch"
          >
            <Button variant="secondary" onClick={onCancel} borderRadius="small">
              Cancel
            </Button>
            <Button variant="primary" type="submit" borderRadius="small">
              Save Changes
            </Button>
          </Flex>
        </form>
      </Flex>
    </Modal>
  );
};

export default CheckBoxUpdateModal;
